import { Icon } from '../../../../widgets/icon';
import { NotificationBanner } from '../../../../widgets/notificationBanner';
import React, { FC, useEffect, useState } from 'react';
import { useLazyLoadQuery } from 'react-relay';
import { graphql } from 'relay-runtime';
import { T, toGlobalID } from 'src/shared/helpers';
import { useFeatureFlagWithConditions } from 'src/shared/feature-flags';
import { useAccountData } from 'src/shared/hooks/useAccountData';
import CloseIcon from 'src/shared/material_icons/navigation/close.svg';
import styled from 'styled-components';
import { useAIStudioProduct } from '../context/productContext';
import { productIframeQuery } from './__generated__/productIframeQuery.graphql';

const OuterWrapper = styled.div<{ $hidden?: boolean }>`
    flex: 1;
    display: flex;
    flex-direction: column;
    max-width: calc(100% - 300px);
    width: 100%;
    margin: 30px auto;
    min-height: 0;

    @media (max-width: 1366px) {
        max-width: calc(100% - 50px);
        margin: 25px auto;
    }

    ${(props) =>
        props.$hidden &&
        `
		position: absolute;
		width: 0;
		height: 0;
		overflow: hidden;
		pointer-events: none;
	`}
`;

const BannerSection = styled.div`
    flex-shrink: 0;
    padding: 0 0 12px 0;
`;

const IframeContainer = styled.div`
    flex: 1;
    position: relative;
    background: white;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    border-radius: 16px;
    overflow: hidden;
    min-height: 0;
`;

const StyledIframe = styled.iframe`
    width: 100%;
    height: 100%;
    border: 0;
`;

const CloseButton = styled.button`
    position: absolute;
    top: 12px;
    right: 12px;
    z-index: 10;
    width: 36px;
    height: 36px;
    border: none;
    border-radius: 50%;
    background: rgba(0, 0, 0, 0.5);
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s;

    &:hover {
        background: rgba(0, 0, 0, 0.7);
        transform: scale(1.1);
    }

    ${Icon} {
        width: 16px;
        height: 16px;
        min-width: 16px;
        color: white;
    }
`;

const BANNER_DISMISSED_KEY = 'aiStudioIframeBannerDismissed';

interface ProductIframeProps {
    productId: number;
    /** Whether the iframe should be visible (false = hidden but kept alive in DOM) */
    visible?: boolean;
    /** Callback when user clicks the close button on the iframe */
    onClose?: () => void;
}

export const ProductIframe: FC<ProductIframeProps> = ({ productId, visible = true, onClose }) => {
    const { updateProduct } = useAIStudioProduct();
    const { userInfo } = useAccountData();
    const isPrototypeModeEnabled = useFeatureFlagWithConditions('prototypeMode', {
        userId: (userInfo?.userID ?? 0).toString()
    });
    const [showBanner, setShowBanner] = useState(() => localStorage.getItem(BANNER_DISMISSED_KEY) !== 'true');
    const data = useLazyLoadQuery<productIframeQuery>(
        graphql`
            query productIframeQuery($productId: ID!) {
                model(id: $productId) {
                    id
                    zakekeEnvironment
                    iframeUrl {
                        aiStudioCustomizerUrl
                        aiStudioConfiguratorUrl
                        aiStudioViewerUrl
                    }
                }
            }
        `,
        { productId: toGlobalID(productId, 'Model')! }
    );

    const env = data.model?.zakekeEnvironment;

    // Push the environment type into the product context
    useEffect(() => {
        if (env && env !== 'UNKNOWN') {
            updateProduct({ environment: env });
        }
    }, [env, updateProduct]);

    const iframeUrl = (() => {
        const urls = data.model?.iframeUrl;
        if (!urls) return null;
        switch (data.model?.zakekeEnvironment) {
            case 'COMPOSER':
                return urls.aiStudioConfiguratorUrl;
            case 'VIEWER':
                return urls.aiStudioViewerUrl;
            case 'CUSTOMIZER':
            default:
                if (!isPrototypeModeEnabled || !urls.aiStudioCustomizerUrl) return urls.aiStudioCustomizerUrl;
                const url = new URL(urls.aiStudioCustomizerUrl, window.location.origin);
                url.searchParams.set('isPrototype', '1');
                return url.toString();
        }
    })();

    if (!iframeUrl) {
        return null;
    }

    return (
        <OuterWrapper $hidden={!visible}>
            {visible && showBanner && (
                <BannerSection>
                    <NotificationBanner
                        title={T._('Why am I seeing this?', 'Admin')}
                        type='INFO'
                        showCloseButton
                        onClose={() => {
                            setShowBanner(false);
                            localStorage.setItem(BANNER_DISMISSED_KEY, 'true');
                        }}
                        content={T._(
                            'You selected a product from your Zakeke catalog. You can now customize it or position it in 3D. The version you create here will be used as the base for content generation in AI Agents Studio. If you want to use the product as is, simply click "Use in AI Studio."',
                            'Admin'
                        )}
                    />
                </BannerSection>
            )}
            <IframeContainer>
                {visible && onClose && (
                    <CloseButton onClick={onClose}>
                        <Icon>
                            <CloseIcon />
                        </Icon>
                    </CloseButton>
                )}
                <StyledIframe src={iframeUrl} allow='camera; microphone; autoplay; fullscreen; picture-in-picture' allowFullScreen />
            </IframeContainer>
        </OuterWrapper>
    );
};

export default ProductIframe;
